import { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Hook for real-time job updates via SSE.
 * Connects to /api/jobs/:jobId/live and receives bid/counter/accept events.
 */
export function useLiveJob(jobId, { onNewBid, onBidAccepted, onCounterOffer } = {}) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const esRef = useRef(null);
  const callbacksRef = useRef({ onNewBid, onBidAccepted, onCounterOffer });

  // Keep callbacks ref updated
  callbacksRef.current = { onNewBid, onBidAccepted, onCounterOffer };

  useEffect(() => {
    if (!jobId) return;

    const url = `${API}/jobs/${jobId}/live`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onopen = () => setConnected(true);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastEvent(data);

        if (data.type === 'new_bid' && callbacksRef.current.onNewBid) {
          callbacksRef.current.onNewBid(data.bid);
        } else if (data.type === 'bid_accepted' && callbacksRef.current.onBidAccepted) {
          callbacksRef.current.onBidAccepted(data.bid_id);
        } else if (data.type === 'counter_offer' && callbacksRef.current.onCounterOffer) {
          callbacksRef.current.onCounterOffer(data);
        }
      } catch {}
    };

    es.onerror = () => {
      setConnected(false);
      // EventSource auto-reconnects
    };

    return () => {
      es.close();
      esRef.current = null;
      setConnected(false);
    };
  }, [jobId]);

  return { connected, lastEvent };
}

/**
 * Hook for user-level live updates (my jobs bid counts, new jobs in feed).
 */
export function useLiveUser(token, { onBidReceived } = {}) {
  const [connected, setConnected] = useState(false);
  const callbacksRef = useRef({ onBidReceived });
  callbacksRef.current = { onBidReceived };

  useEffect(() => {
    if (!token) return;

    // EventSource doesn't support custom headers, so we use a polling fallback
    // that checks for new events via the SSE endpoint with auth in URL
    // For production, use a WebSocket or cookie-based auth
    // Here we use fetch-based SSE polling as EventSource can't send Bearer tokens
    let active = true;

    async function connect() {
      try {
        const resp = await fetch(`${API}/live/user`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/event-stream' },
        });
        if (!resp.ok || !resp.body) return;

        setConnected(true);
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (active) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'bid_received' && callbacksRef.current.onBidReceived) {
                  callbacksRef.current.onBidReceived(data);
                }
              } catch {}
            }
          }
        }
      } catch {} finally {
        setConnected(false);
        // Reconnect after 3s
        if (active) setTimeout(connect, 3000);
      }
    }

    connect();
    return () => { active = false; };
  }, [token]);

  return { connected };
}
